import { Modal, Form, Input, Button, Popconfirm, Checkbox, notification } from 'antd'
import { useState, useEffect } from 'react'
import { DeleteOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { Duration } from '../../common/durationModel'
import {
  useUpsertTaskEventMutation,
  useRemoveScheduledEventMutation,
  useRemoveScheduledEventsMutation,
  useRemoveScheduledEventsByRangeMutation,
  useGetRepeatingEventConfigQuery,
  useUpdateScheduledEventMutation,
  useUpdateFutureScheduledEventsMutation,
  useUpdateScheduledEventsRangeMutation,
  useSetTaskCompletionMutation,
} from '../scheduleQueries'
import { useEventFormState } from '../EventForm/useEventFormState'
import EventFormScheduleFields from '../EventForm/EventFormScheduleFields'
import EventFormCrewFields from '../EventForm/EventFormCrewFields'
import {
  parseRepeatingEventConfig,
  defaultRecurringFormValues,
  buildRepeatingEventConfig,
  buildTimingFields,
} from '../EventForm/recurringEventFormHelpers'
import { deriveEndDateMode, reconcileFormValues } from '../EventForm/eventFormModel'
import { TaskCalendarEvent, TaskCompletionScopes } from '../scheduleApi'
import { hasNonCompletionChanges, mapApplyChangesOptionToScope, TaskEditSnapshot } from './taskEventModalModel'
import { findCrewMemberByEmail } from '../crewModel'
import { useSchedule } from '../ScheduleContext'
import ScheduledEventsChangesModal, { ApplyChangesOption } from '../JobDrawer/Modals/ScheduledEventsChangesModal'

type Props = {
  open: boolean
  onClose: () => void
  event?: TaskCalendarEvent | null
  selectedDate?: Dayjs
  onSuccess?: () => void
}

const TaskEventModal = ({ open, onClose, event, selectedDate: initialDate, onSuccess }: Props) => {
  const isEditing = !!event
  const isRecurring = !!event?.repeatingId
  const { crew } = useSchedule()
  const formState = useEventFormState({
    jobId: 0,
    defaultDate: initialDate,
    defaultDurationMinutes: 60,
  })

  const { form, formValues, setFormValues, areRecurringFieldsValid } = formState
  const upsertTaskMutation = useUpsertTaskEventMutation()
  const deleteMutation = useRemoveScheduledEventMutation()
  const deleteSeriesMutation = useRemoveScheduledEventsMutation()
  const deleteByRangeMutation = useRemoveScheduledEventsByRangeMutation()
  const updateEventMutation = useUpdateScheduledEventMutation()
  const updateFutureEventsMutation = useUpdateFutureScheduledEventsMutation()
  const updateEventsRangeMutation = useUpdateScheduledEventsRangeMutation()
  const setTaskCompletionMutation = useSetTaskCompletionMutation()
  const [title, setTitle] = useState('')
  const [notes, setNotes] = useState('')
  const [frozen, setFrozen] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [initialSnapshot, setInitialSnapshot] = useState<TaskEditSnapshot | null>(null)
  const [showChangesModal, setShowChangesModal] = useState(false)
  const [pendingPayload, setPendingPayload] = useState<any>(null)
  const [pendingAction, setPendingAction] = useState<'save' | 'delete' | 'complete'>('save')

  const { data: repeatingEventConfig } = useGetRepeatingEventConfigQuery(event?.repeatingId ?? null)

  useEffect(() => {
    if (!open) {
      return
    }

    if (event) {
      setTitle(event.title ?? '')
      setNotes(event.notes ?? '')
      setFrozen(event.frozen ?? false)
      setCompleted(!!event.completedAt)

      const startDate = dayjs(event.start)
      const endDate = dayjs(event.end)
      const durationMinutes = endDate.diff(startDate, 'minutes')
      const estimatedDuration = Duration.minutes(durationMinutes)

      const crewMemberIds = event.crewMemberIds
        ? event.crewMemberIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id))
        : []

      const selectedCrew = (crew ?? []).filter(m => crewMemberIds.includes(m.id))

      const crewLead = event.crewForemanEmail
        ? findCrewMemberByEmail(crew, event.crewForemanEmail)
        : null

      const recurringValues = (repeatingEventConfig && event.repeatingId)
        ? parseRepeatingEventConfig(repeatingEventConfig, startDate)
        : defaultRecurringFormValues(startDate)

      const populatedValues = {
        selectedCrew,
        crewLeadId: crewLead?.id ?? null,
        selectedWorkareaIds: [],
        isAllDay: event.allDay,
        endDateMode: deriveEndDateMode({ isAllDay: event.allDay, startDate, endDate }),
        startDate,
        endDate,
        startTime: startDate,
        estimatedDuration,
        ...recurringValues,
        sendCrewLeadNotifications: event.sendCrewLeadNotifications,
      }

      setFormValues(populatedValues)
      setInitialSnapshot({
        title: event.title ?? '',
        notes: event.notes ?? '',
        frozen: event.frozen ?? false,
        formValues: reconcileFormValues(formValues, populatedValues),
      })

      form.setFieldsValue({
        startDate,
        startTime: startDate,
        estimatedDuration,
        crewMembers: crewMemberIds,
        repeatUnit: recurringValues.repeatUnit,
        repeatCount: recurringValues.repeatCount,
        repeatPlace: recurringValues.repeatPlace,
        repeatQuarter: recurringValues.repeatQuarter,
        repeatMonth: recurringValues.repeatMonth,
      })
    } else {
      setTitle('')
      setNotes('')
      setFrozen(false)
      setCompleted(false)
      setInitialSnapshot(null)

      const dateToUse = initialDate || dayjs()
      const timeToUse = dayjs().hour(8).minute(0)
      const durationToUse = Duration.minutes(60)
      const recurringDefaults = defaultRecurringFormValues(dateToUse)

      setFormValues({
        selectedCrew: [],
        crewLeadId: null,
        selectedWorkareaIds: [],
        isAllDay: true,
        endDateMode: 'sameDay',
        startDate: dateToUse,
        endDate: dateToUse,
        startTime: timeToUse,
        estimatedDuration: durationToUse,
        ...recurringDefaults,
      })

      form.setFieldsValue({
        startDate: dateToUse,
        startTime: timeToUse,
        estimatedDuration: durationToUse,
        crewMembers: [],
        repeatUnit: recurringDefaults.repeatUnit,
        repeatCount: recurringDefaults.repeatCount,
        repeatPlace: recurringDefaults.repeatPlace,
        repeatQuarter: recurringDefaults.repeatQuarter,
        repeatMonth: recurringDefaults.repeatMonth,
      })
    }
  }, [open, event, crew, form, initialDate, repeatingEventConfig])

  const notifyCompletionNotApplied = () => {
    notification.warning({
      message: 'Completion Not Applied',
      description: 'Rescheduling rebuilds these occurrences, so the completed status was not saved. Reopen the task to mark it complete.',
    })
  }

  const handleClose = () => {
    form.resetFields()
    onClose()
  }

  const handleDelete = async () => {
    if (!event) {
      return
    }

    if (isRecurring) {
      setPendingAction('delete')
      setPendingPayload({ eventId: event.id })
      handleClose()
      setShowChangesModal(true)
      return
    }

    await deleteMutation.mutateAsync(event.id)
    if (onSuccess) {
      onSuccess()
    }
    handleClose()
  }

  const handleSave = async () => {
    try {
      await form.validateFields()

      const { isAllDay, selectedCrew, crewLeadId, sendCrewLeadNotifications, repeatUnit } = formValues

      if (!formValues.startDate) {
        return
      }
      if (!isAllDay && (!formValues.startTime || !formValues.estimatedDuration)) {
        return
      }

      const { actualStartDateTime, endDateTime, durationMinutes } = buildTimingFields(formValues)
      const crewMemberIds = selectedCrew.map(m => m.id).join(',')
      const repeatingEventConfig = buildRepeatingEventConfig(formValues)

      const completionChanged = isEditing && completed !== !!event!.completedAt
      const scheduleChanged = !initialSnapshot || hasNonCompletionChanges(initialSnapshot, {
        title,
        notes,
        frozen,
        formValues,
      })

      if (isEditing && !scheduleChanged) {
        if (!completionChanged) {
          handleClose()
          return
        }

        if (isRecurring) {
          setPendingAction('complete')
          setPendingPayload({ eventId: event!.id, completed })
          handleClose()
          setShowChangesModal(true)
          return
        }

        await setTaskCompletionMutation.mutateAsync({
          eventId: event!.id,
          completed,
          scope: TaskCompletionScopes.ThisEvent,
        })

        if (onSuccess) {
          onSuccess()
        }
        handleClose()
        return
      }

      if (isRecurring) {
        const payload = {
          eventId: event!.id,
          start: actualStartDateTime.toISOString(),
          end: endDateTime.toISOString(),
          dayStartTime: null,
          estimatedDurationInMinutes: durationMinutes,
          crewMemberIds,
          crewLeadId,
          workareaIds: '',
          routeId: null,
          repeatingEventConfig,
          allDay: isAllDay,
          sendCrewLeadNotifications,
          title: title.trim(),
          notes: notes.trim() || null,
          frozen,
        }

        setPendingAction('save')
        setPendingPayload({ updateRequest: payload, completionChanged, completed })
        handleClose()
        setShowChangesModal(true)
        return
      }

      const isCreatingNewRecurrence = repeatUnit !== 'none' && !isRecurring

      await upsertTaskMutation.mutateAsync({
        eventId: event?.id ?? null,
        title: title.trim(),
        notes: notes.trim() || null,
        start: actualStartDateTime.toISOString(),
        end: endDateTime.toISOString(),
        dayStartTime: null,
        estimatedDurationInMinutes: durationMinutes,
        crewLeadId,
        crewMemberIds,
        allDay: isAllDay,
        sendCrewLeadNotifications,
        repeatingEventConfig: isCreatingNewRecurrence ? repeatingEventConfig : null,
        completedAt: event?.completedAt ?? null,
        frozen,
      })

      if (completionChanged) {
        if (isCreatingNewRecurrence) {
          notifyCompletionNotApplied()
        } else {
          await setTaskCompletionMutation.mutateAsync({
            eventId: event!.id,
            completed,
            scope: TaskCompletionScopes.ThisEvent,
          })
        }
      }

      if (onSuccess) {
        onSuccess()
      }
      handleClose()
    } catch (_error) {
    }
  }

  const handleApplyChanges = async (option: ApplyChangesOption, dateRange?: [Dayjs, Dayjs] | null) => {
    try {
      if (pendingAction === 'delete') {
        const eventId = pendingPayload?.eventId
        if (!eventId) {
          return
        }

        if (option === 'this-event') {
          await deleteMutation.mutateAsync(eventId)
        } else if (option === 'this-and-future') {
          await deleteSeriesMutation.mutateAsync(eventId)
        } else if (option === 'selected-dates' && dateRange) {
          await deleteByRangeMutation.mutateAsync({
            eventId,
            request: {
              start: dateRange[0].toISOString(),
              end: dateRange[1].toISOString(),
              jobId: 0,
            },
          })
        }
      } else if (pendingAction === 'complete') {
        if (!pendingPayload) {
          return
        }

        await setTaskCompletionMutation.mutateAsync({
          eventId: pendingPayload.eventId,
          completed: pendingPayload.completed,
          scope: mapApplyChangesOptionToScope(option),
          start: dateRange ? dateRange[0].toISOString() : null,
          end: dateRange ? dateRange[1].toISOString() : null,
        })
      } else {
        if (!pendingPayload) {
          return
        }

        const { updateRequest, completionChanged, completed: pendingCompleted } = pendingPayload

        if (option === 'this-event') {
          await updateEventMutation.mutateAsync(updateRequest)
        } else if (option === 'selected-dates' && dateRange) {
          await updateEventsRangeMutation.mutateAsync({
            updateRequest,
            start: dateRange[0].toISOString(),
            end: dateRange[1].toISOString(),
          })
        } else if (option === 'this-and-future') {
          await updateFutureEventsMutation.mutateAsync(updateRequest)
        }

        if (completionChanged) {
          if (option === 'this-event') {
            await setTaskCompletionMutation.mutateAsync({
              eventId: updateRequest.eventId,
              completed: pendingCompleted,
              scope: TaskCompletionScopes.ThisEvent,
            })
          } else {
            notifyCompletionNotApplied()
          }
        }
      }

      if (onSuccess) {
        onSuccess()
      }
      setShowChangesModal(false)
      setPendingPayload(null)
    } catch (_error) {
    }
  }

  const isFormValid = title.trim().length > 0 && areRecurringFieldsValid()

  return (
    <>
      <Modal
        open={open}
        onCancel={handleClose}
        width={600}
        title={isEditing ? "Edit Task" : "Schedule Task"}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              {isEditing && (
                isRecurring ? (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    loading={deleteMutation.isLoading || deleteSeriesMutation.isLoading}
                    onClick={handleDelete}
                  >
                    Remove from Schedule
                  </Button>
                ) : (
                  <Popconfirm
                    title="Remove from Schedule"
                    description="Are you sure you want to remove this task from the schedule?"
                    onConfirm={handleDelete}
                    okText="Yes, Remove"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      loading={deleteMutation.isLoading}
                    >
                      Remove from Schedule
                    </Button>
                  </Popconfirm>
                )
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleSave}
                disabled={!isFormValid || upsertTaskMutation.isLoading || setTaskCompletionMutation.isLoading}
                loading={upsertTaskMutation.isLoading || setTaskCompletionMutation.isLoading}
              >
                Save Task
              </Button>
            </div>
          </div>
        }
      >
        <Form form={form} layout="vertical" style={{ marginTop: "24px" }}>
          <Form.Item label="Title" required style={{ marginBottom: "16px" }}>
            <Input
              placeholder="Title"
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Notes" style={{ marginBottom: "16px" }}>
            <Input.TextArea
              placeholder="Notes (optional)"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
            />
          </Form.Item>
          {isEditing && (
            <Form.Item style={{ marginBottom: "16px" }}>
              <Checkbox
                checked={completed}
                onChange={e => setCompleted(e.target.checked)}
              >
                Mark as completed
              </Checkbox>
            </Form.Item>
          )}
          <Form.Item style={{ marginBottom: "16px" }}>
            <Checkbox
              checked={frozen}
              onChange={e => setFrozen(e.target.checked)}
            >
              This task is frozen and cannot be rescheduled
            </Checkbox>
          </Form.Item>
          <EventFormScheduleFields
            formState={formState}
          />
          <EventFormCrewFields formState={formState} showNotificationOption={false} />
        </Form>
      </Modal>
      <ScheduledEventsChangesModal
        open={showChangesModal}
        onClose={() => {
          setShowChangesModal(false)
          setPendingPayload(null)
        }}
        onConfirm={handleApplyChanges}
        title={event?.title ?? 'Task'}
        subtitle="Task"
        allowPastDates={pendingAction === 'complete'}
      />
    </>
  )
}

export default TaskEventModal
